import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Network, FileText, Stethoscope, Loader2, Plus, Trash2, Save,
  ExternalLink, RefreshCw, Database, X, Image as ImageIcon,
  BookOpen, AlertCircle, Link2, Search, GitBranch,
  ZoomIn, ZoomOut, RotateCw, Maximize2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster, toast } from "@/components/ui/sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import ForceGraph3D from "react-force-graph-3d";
import SpriteText from "three-spritetext";
import { createPortal } from "react-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = { withCredentials: true };

// Estrae un messaggio stringa da un errore axios, gestendo anche il
// `detail` array di Pydantic (422) che altrimenti farebbe crashare React.
function errMsg(e, fallback = "Errore") {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => (x?.msg || "").replace(/^Value error, /, "")).join("; ") || fallback;
  if (typeof d === "string") return d;
  return fallback;
}

const COLOR = { prestazione: "#818cf8", case: "#34d399", article: "#fbbf24", rule: "#c084fc", page: "#22d3ee" };
const TYPE_LABEL = { prestazione: "Prestazione", case: "Caso clinico", article: "Articolo", rule: "Regola dottore", page: "Pagina correlata" };

// ─── COMPONENTE 1 — Mappa della conoscenza (grafo 3D sferico navigabile) ─────
function KnowledgeGraph() {
  const areaRef = useRef(null);
  const wrapRef = useRef(null);
  const fgRef = useRef(null);
  const mountedRef = useRef(true);
  const lastCountRef = useRef(null);
  const rotRef = useRef(null);
  const [rawData, setRawData] = useState({ nodes: [], links: [] });
  const [showPages, setShowPages] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [relSel, setRelSel] = useState(null);
  const [selected, setSelected] = useState(null);
  const [caseDetail, setCaseDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [clusterId, setClusterId] = useState(null);
  const [side, setSide] = useState(560);

  useEffect(() => {
    mountedRef.current = true;
    const measure = () => {
      const avail = areaRef.current ? areaRef.current.clientWidth : 560;
      // Area quadrata larga quanto la colonna della pagina.
      setSide(Math.round(Math.max(360, avail)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("resize", measure);
      if (rotRef.current) clearInterval(rotRef.current);
    };
  }, []);

  const loadGraph = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/knowledge/graph`, ax);
      if (!mountedRef.current) return;
      setStats(data.stats);
      lastCountRef.current = data.stats?.cases ?? 0;
      const nodes = (data.nodes || []).map((n) => ({
        id: n.id, type: n.type, label: n.label || "", _raw: n,
        color: COLOR[n.type] || "#94a3b8",
        val: n.type === "prestazione" ? 9 : n.type === "page" ? 2.5 : 4,
      }));
      const links = (data.edges || []).map((e) => ({
        source: e.from, target: e.to, rel: !!e.rel, weight: e.weight || 1,
        strong: !!e.strong, page: !!e.page,
        pair: e.pair || null, sources: e.sources || 0, items: e.source_items || [],
      }));
      setRawData({ nodes, links });
    } catch (e) {
      if (mountedRef.current) toast.error("Errore nel caricamento del grafo");
    } finally {
      setLoading(false);
    }
  }, []);

  // Vista filtrata: vista a cluster (isola una prestazione) > tipo-nodo > pagine.
  const graphData = useMemo(() => {
    let clusterSet = null;
    if (clusterId) {
      clusterSet = new Set([clusterId]);
      rawData.links.forEach((l) => {
        if (l.rel && (l.source === clusterId || l.target === clusterId)) {
          clusterSet.add(l.source === clusterId ? l.target : l.source);
        } else if (!l.rel) {
          if (l.target === clusterId) clusterSet.add(l.source);
          else if (l.source === clusterId) clusterSet.add(l.target);
        }
      });
    }
    const vis = (n) => {
      if (n.type === "page" && !showPages) return false;
      if (clusterSet) return clusterSet.has(n.id);
      return !hidden.has(n.type);
    };
    const nodes = rawData.nodes.filter(vis).map((n) => ({ ...n }));
    const ids = new Set(nodes.map((n) => n.id));
    const links = rawData.links
      .filter((l) => ids.has(l.source) && ids.has(l.target))
      .map((l) => ({ ...l }));
    return { nodes, links };
  }, [rawData, showPages, hidden, clusterId]);

  // Set di nodi che corrispondono alla ricerca (per etichetta o concetto).
  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const ids = rawData.nodes
      .filter((n) => (n.label || "").toLowerCase().includes(q)
        || (n._raw?.concepts || []).some((c) => c.toLowerCase().includes(q)))
      .map((n) => n.id);
    return new Set(ids);
  }, [query, rawData]);

  const toggleType = useCallback((t) => {
    if (t === "page") { setShowPages((v) => !v); return; }
    setHidden((prev) => { const s = new Set(prev); s.has(t) ? s.delete(t) : s.add(t); return s; });
  }, []);

  // ─── Controlli di navigazione del grafo 3D ───
  const zoomBy = useCallback((factor) => {
    const fg = fgRef.current; if (!fg) return;
    const p = fg.cameraPosition();
    fg.cameraPosition({ x: p.x * factor, y: p.y * factor, z: p.z * factor }, undefined, 300);
  }, []);

  const recenter = useCallback(() => {
    try { fgRef.current && fgRef.current.zoomToFit(600, 60); } catch { /* noop */ }
  }, []);

  const toggleRotate = useCallback(() => {
    if (rotRef.current) {
      clearInterval(rotRef.current); rotRef.current = null; setRotating(false); return;
    }
    setRotating(true);
    rotRef.current = setInterval(() => {
      const fg = fgRef.current; if (!fg) return;
      const p = fg.cameraPosition();
      const a = 0.006;
      const x = p.x * Math.cos(a) - p.z * Math.sin(a);
      const z = p.x * Math.sin(a) + p.z * Math.cos(a);
      fg.cameraPosition({ x, y: p.y, z });
    }, 30);
  }, []);

  // Trascina la vista (pan): disponibile col drag del mouse (nessun tasto).

  const isolateCluster = useCallback((id) => {
    setClusterId(id);
    setSelected(null); setCaseDetail(null); setRelSel(null); setQuery("");
  }, []);

  // Dopo l'ingresso in vista cluster, adatta la camera ai nodi rimasti.
  useEffect(() => {
    if (!clusterId) return;
    const t = setTimeout(() => { try { fgRef.current && fgRef.current.zoomToFit(600, 80); } catch { /* noop */ } }, 450);
    return () => clearTimeout(t);
  }, [clusterId]);

  const handleNodeClick = useCallback(async (node) => {
    const raw = node?._raw;
    if (!raw) return;
    if (fgRef.current && node.x != null) {
      const dist = 140;
      const ratio = 1 + dist / Math.max(1, Math.hypot(node.x, node.y, node.z || 0.01));
      fgRef.current.cameraPosition(
        { x: node.x * ratio, y: node.y * ratio, z: (node.z || 0) * ratio }, node, 900,
      );
    }
    setSelected(raw);
    setCaseDetail(null);
    if (raw.type === "case") {
      const oppId = raw.id.replace("case:", "");
      try {
        const { data: d } = await axios.get(`${API}/api/knowledge/cases/${oppId}`, ax);
        if (mountedRef.current) setCaseDetail(d);
      } catch { /* ignore */ }
    }
  }, []);

  const handleLinkClick = useCallback((l) => {
    if (!l || !l.rel) return;
    setSelected(null); setCaseDetail(null);
    setRelSel({
      pair: l.pair || [], weight: l.weight || 1, sources: l.sources || 0,
      strong: !!l.strong, items: l.items || [],
    });
  }, []);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  // Centra la camera sul primo nodo che corrisponde alla ricerca.
  useEffect(() => {
    if (!matched || matched.size === 0 || !fgRef.current) return;
    const node = graphData.nodes.find((n) => matched.has(n.id) && n.x != null);
    if (!node) return;
    const dist = 160;
    const ratio = 1 + dist / Math.max(1, Math.hypot(node.x, node.y, node.z || 0.01));
    try {
      fgRef.current.cameraPosition(
        { x: node.x * ratio, y: node.y * ratio, z: (node.z || 0) * ratio }, node, 800,
      );
    } catch { /* noop */ }
  }, [matched, graphData]);

  // Auto-aggiornamento: ricarica quando cambia il numero di casi indicizzati
  // (nuovo piano salvato, job notturno o indicizzazione manuale).
  useEffect(() => {
    const iv = setInterval(async () => {
      if (!mountedRef.current) return;
      try {
        const { data } = await axios.get(`${API}/api/knowledge/index/status`, ax);
        const n = data?.indexed_cases;
        if (lastCountRef.current != null && typeof n === "number" && n !== lastCountRef.current) {
          lastCountRef.current = n;
          toast.info("Mappa aggiornata con i nuovi casi");
          loadGraph();
        }
      } catch { /* silenzioso */ }
    }, 6000);
    return () => clearInterval(iv);
  }, [loadGraph]);

  const isEmpty = stats && stats.cases + stats.articles + stats.rules === 0;

  return (
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="kb-graph-search"
            placeholder="Cerca prestazione, caso, articolo…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 pl-8 text-sm"
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="kb-graph-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {matched && <span className="text-xs text-slate-400">{matched.size} risultati</span>}
        <span className="text-xs text-slate-400 hidden sm:inline ml-auto">Trascina per ruotare · scroll per zoom · clicca una linea per le fonti</span>
        <Button size="sm" variant="outline" onClick={loadGraph} data-testid="kb-graph-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
        </Button>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {stats && (
          <>
            <FilterBadge color={COLOR.prestazione} label={`${stats.prestazioni} prestazioni`} active={!hidden.has("prestazione")} onClick={() => toggleType("prestazione")} testid="kb-filter-prestazione" />
            <FilterBadge color={COLOR.case} label={`${stats.cases} casi`} active={!hidden.has("case")} onClick={() => toggleType("case")} testid="kb-filter-case" />
            <FilterBadge color={COLOR.article} label={`${stats.articles} articoli`} active={!hidden.has("article")} onClick={() => toggleType("article")} testid="kb-filter-article" />
            <FilterBadge color={COLOR.rule} label={`${stats.rules} regole`} active={!hidden.has("rule")} onClick={() => toggleType("rule")} testid="kb-filter-rule" />
            {stats.relazioni > 0 && <Badge color="#818cf8" label={`${stats.relazioni} relazioni`} />}
            {stats.relazioni_forti > 0 && <Badge color="#6366f1" label={`${stats.relazioni_forti} relazioni forti`} />}
            {stats.pagine > 0 && <FilterBadge color={COLOR.page} label={`${stats.pagine} pagine`} active={showPages} onClick={() => toggleType("page")} testid="kb-filter-page" />}
          </>
        )}
      </div>
      {clusterId && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100" data-testid="kb-cluster-banner">
          <GitBranch className="w-4 h-4 text-indigo-600 flex-shrink-0" />
          <span className="text-sm text-indigo-800 truncate">
            Vista cluster: <b>{clusterId.replace("prest:", "")}</b>
            <span className="text-indigo-500 ml-1">· {graphData.nodes.length} nodi collegati</span>
          </span>
          <button onClick={() => setClusterId(null)} data-testid="kb-cluster-exit" className="ml-auto text-xs font-medium text-indigo-700 hover:underline flex items-center gap-1 flex-shrink-0">
            <X className="w-3.5 h-3.5" /> Esci dalla vista
          </button>
        </div>
      )}
      <div ref={areaRef} className="flex justify-center">
      <div ref={wrapRef} data-testid="kb-graph-canvas" className="relative rounded-xl border border-slate-800 overflow-hidden" style={{ width: side, height: side, background: "#0b1020" }}>
        {!loading && !isEmpty && (
          <ForceGraph3D
            ref={fgRef}
            width={side}
            height={side}
            graphData={graphData}
            backgroundColor="#0b1020"
            showNavInfo={false}
            nodeLabel={(n) => `${TYPE_LABEL[n.type]}: ${n.label}`}
            nodeColor={(n) => (matched && !matched.has(n.id) ? "#334155" : n.color)}
            nodeVal={(n) => n.val}
            nodeOpacity={0.95}
            linkColor={(l) => (l.page ? "rgba(34,211,238,0.45)" : l.rel ? (l.strong ? "rgba(99,102,241,0.85)" : "rgba(129,140,248,0.45)") : "rgba(148,163,184,0.3)")}
            linkWidth={(l) => (l.rel ? Math.min(4, 0.8 + l.weight * 0.6) : l.page ? 0.3 : 0.4)}
            linkOpacity={0.4}
            nodeThreeObjectExtend={true}
            nodeThreeObject={(n) => {
              const txt = n.label.length > 24 ? n.label.slice(0, 22) + "…" : n.label;
              const s = new SpriteText(txt);
              s.color = matched && !matched.has(n.id) ? "#475569" : n.color;
              s.textHeight = n.type === "prestazione" ? 5 : n.type === "page" ? 2.5 : 3.5;
              s.position.set(0, n.type === "prestazione" ? 9 : n.type === "page" ? 4 : 6, 0);
              return s;
            }}
            onNodeClick={handleNodeClick}
            onLinkClick={handleLinkClick}
            onBackgroundClick={() => { setSelected(null); setCaseDetail(null); setRelSel(null); }}
            onEngineStop={() => { try { fgRef.current && fgRef.current.zoomToFit(700, 60); } catch { /* noop */ } }}
          />
        )}
        {/* Controlli di navigazione overlay */}
        {!loading && !isEmpty && (
          <div className="absolute bottom-4 right-4 flex flex-col items-end gap-1.5 select-none" data-testid="kb-graph-controls">
            <NavBtn onClick={() => zoomBy(0.8)} testid="kb-nav-zoom-in" title="Ingrandisci"><ZoomIn className="w-4 h-4" /></NavBtn>
            <NavBtn onClick={() => zoomBy(1.25)} testid="kb-nav-zoom-out" title="Rimpicciolisci"><ZoomOut className="w-4 h-4" /></NavBtn>
            <NavBtn onClick={toggleRotate} testid="kb-nav-rotate" title={rotating ? "Ferma rotazione" : "Ruota"} active={rotating}><RotateCw className={`w-4 h-4 ${rotating ? "animate-spin" : ""}`} /></NavBtn>
            <NavBtn onClick={recenter} testid="kb-nav-recenter" title="Adatta alla vista"><Maximize2 className="w-4 h-4" /></NavBtn>
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-300" />
          </div>
        )}
        {!loading && isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-300 px-6 text-center">
            Nessun nodo ancora. Indicizza i piani di cura e aggiungi articoli/regole per popolare la mappa.
          </div>
        )}
      </div>
      </div>


      {/* Scheda laterale dettaglio nodo (in portale su body per non finire
          sotto la Navbar a causa dello stacking context) */}
      {selected && createPortal((
        <div className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-[60] overflow-y-auto border-l border-slate-200" data-testid="kb-node-panel">
          <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">{TYPE_LABEL[selected.type]}</div>
              <div className="font-semibold text-slate-900">{selected.label}</div>
            </div>
            <button onClick={() => { setSelected(null); setCaseDetail(null); }} data-testid="kb-node-panel-close" className="text-slate-400 hover:text-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            {selected.type === "prestazione" && (
              <Button onClick={() => isolateCluster(selected.id)} data-testid="kb-isolate-btn" size="sm" className="w-full">
                <GitBranch className="w-4 h-4 mr-1.5" /> Isola questa prestazione
              </Button>
            )}
            {(selected.concepts || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selected.concepts.map((c) => (
                  <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{c}</span>
                ))}
              </div>
            )}
            {selected.type === "article" && (
              <div className="space-y-2 text-sm">
                {selected.meta?.description && <p className="text-slate-600">{selected.meta.description}</p>}
                <a href={selected.meta?.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:underline break-all">
                  <ExternalLink className="w-4 h-4" /> {selected.meta?.url}
                </a>
              </div>
            )}
            {selected.type === "page" && (
              <div className="space-y-2 text-sm">
                {selected.meta?.parent_article && (
                  <p className="text-slate-500 text-xs">Da: <span className="font-medium text-slate-700">{selected.meta.parent_article}</span></p>
                )}
                <a href={selected.meta?.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-600 hover:underline break-all">
                  <ExternalLink className="w-4 h-4" /> {selected.meta?.url}
                </a>
              </div>
            )}
            {selected.type === "rule" && (
              <div className="text-sm text-slate-700 whitespace-pre-wrap bg-purple-50 border border-purple-100 rounded-lg p-3">
                {selected.meta?.rules_text || "—"}
              </div>
            )}
            {selected.type === "case" && (
              <div className="space-y-3 text-sm">
                {!caseDetail && <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />}
                {caseDetail && (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <Info k="Medico" v={caseDetail.medico_name} />
                      <Info k="Assistente" v={caseDetail.assistente_name} />
                      <Info k="Assicurazione" v={caseDetail.assicurazione_name} />
                      <Info k="Totale" v={caseDetail.total_cost ? `€ ${caseDetail.total_cost}` : "—"} />
                    </div>
                    {(caseDetail.prestazioni || []).length > 0 && (
                      <div>
                        <div className="font-semibold text-slate-700 mb-1">Prestazioni</div>
                        <ul className="space-y-0.5">
                          {caseDetail.prestazioni.map((p, i) => (
                            <li key={i} className="text-slate-600">• {p.tooth ? `[${p.tooth}] ` : ""}{p.name} {p.euro ? `— € ${p.euro}` : ""}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(caseDetail.photo_analyses || []).length > 0 && (
                      <div>
                        <div className="font-semibold text-slate-700 mb-1">Analisi foto (AI)</div>
                        {caseDetail.photo_analyses.map((a, i) => (
                          <div key={i} className="mb-2 text-xs bg-slate-50 border rounded-lg p-2">
                            {a.tipo_caso && <div className="font-medium text-slate-800">{a.tipo_caso}</div>}
                            {a.descrizione && <div className="text-slate-600">{a.descrizione}</div>}
                            {a.relazioni && <div className="text-indigo-600 mt-1"><span className="font-medium">Relazioni:</span> {a.relazioni}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {(caseDetail.photo_urls || []).length > 0 && (
                      <div>
                        <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><ImageIcon className="w-4 h-4" /> Foto del caso</div>
                        <div className="grid grid-cols-2 gap-2">
                          {caseDetail.photo_urls.map((u, i) => (
                            <a key={i} href={u} target="_blank" rel="noreferrer">
                              <img src={u} alt="" className="w-full h-24 object-cover rounded-lg border" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ), document.body)}

      {/* Pannello dettaglio relazione (click su una linea tra prestazioni) */}
      {relSel && createPortal((
        <div className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-[60] overflow-y-auto border-l border-slate-200" data-testid="kb-rel-panel">
          <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" /> Relazione</div>
              <div className="font-semibold text-slate-900">{(relSel.pair || []).join("  ↔  ") || "—"}</div>
            </div>
            <button onClick={() => setRelSel(null)} data-testid="kb-rel-panel-close" className="text-slate-400 hover:text-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                Co-occorrenze: <b>{relSel.weight}</b>
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                {relSel.sources} fonti distinte
              </span>
              {relSel.strong && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100 font-medium">
                  Relazione forte
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">
              Queste due prestazioni compaiono insieme nelle fonti seguenti.
            </p>
            <div>
              <div className="font-semibold text-slate-700 mb-2 text-sm">Documentata da ({(relSel.items || []).length})</div>
              {(relSel.items || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nessuna fonte collegata.</p>
              ) : (
                <div className="space-y-1.5">
                  {relSel.items.map((it) => (
                    <button
                      key={it.id}
                      data-testid="kb-rel-source-item"
                      onClick={() => { setRelSel(null); handleNodeClick({ _raw: rawData.nodes.find((n) => n.id === it.id)?._raw, id: it.id }); }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors"
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLOR[it.type] || "#94a3b8" }} />
                      <span className="text-sm text-slate-700 truncate">{it.label}</span>
                      <span className="text-[11px] text-slate-400 ml-auto flex-shrink-0">{TYPE_LABEL[it.type]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

function NavBtn({ onClick, title, testid, active, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center rounded-lg border backdrop-blur-md transition-colors ${active ? "bg-indigo-500/80 border-indigo-400 text-white" : "bg-slate-800/60 border-slate-600/60 text-slate-200 hover:bg-slate-700/80 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

function FilterBadge({ color, label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={active ? "Clicca per nascondere" : "Clicca per mostrare"}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border transition-colors ${active ? "text-slate-700 border-slate-200 bg-white hover:bg-slate-50" : "text-slate-300 border-slate-100 bg-slate-50 line-through"}`}
    >
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: active ? color : "#cbd5e1" }} />{label}
    </button>
  );
}

function Badge({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />{label}
    </span>
  );
}
function Info({ k, v }) {
  return <div><span className="text-slate-400">{k}:</span> <span className="text-slate-700 font-medium">{v || "—"}</span></div>;
}

// ─── COMPONENTE 2 — Archivio articoli scientifici ───────────────────────────
function ArticlesArchive({ isAdmin }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ url: "", title: "", description: "", tags: "", crawl_depth: 2, crawl_max_pages: 25 });
  const [saving, setSaving] = useState(false);
  const [readerId, setReaderId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/knowledge/articles`, ax);
      setArticles(data.articles || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Ricarica periodicamente finché qualche articolo è ancora "pending"
  // (il contenuto viene scaricato in background dopo il salvataggio).
  useEffect(() => {
    if (!articles.some((a) => (a.fetch_status || "pending") === "pending")) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [articles, load]);

  const save = async () => {
    if (!form.url.trim() || !form.title.trim()) { toast.error("URL e titolo sono obbligatori"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/knowledge/articles`, {
        url: form.url, title: form.title, description: form.description,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        crawl_depth: Number(form.crawl_depth) || 2,
        crawl_max_pages: Number(form.crawl_max_pages),
      }, ax);
      toast.success("Articolo salvato — sto leggendo il contenuto…");
      setForm({ url: "", title: "", description: "", tags: "", crawl_depth: 2, crawl_max_pages: 25 });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore nel salvataggio"));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/api/knowledge/articles/${id}`, ax); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const refetch = async (id) => {
    try { await axios.post(`${API}/api/knowledge/articles/${id}/refetch`, {}, ax); toast.info("Rilettura avviata"); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {isAdmin && (
        <Card className="lg:col-span-1 h-fit">
          <CardContent className="p-5 space-y-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo articolo</h3>
            <Input data-testid="kb-article-url" placeholder="URL (https://...)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <Input data-testid="kb-article-title" placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea data-testid="kb-article-desc" placeholder="Descrizione" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Input data-testid="kb-article-tags" placeholder="Tag prestazione (es. impianto, corona)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            <div className="rounded-lg border border-slate-200 p-3 space-y-2.5 bg-slate-50/60">
              <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Scansione pagine correlate</div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs text-slate-500">Profondità (livelli)</label>
                <Select value={String(form.crawl_depth)} onValueChange={(v) => setForm({ ...form, crawl_depth: Number(v) })}>
                  <SelectTrigger data-testid="kb-article-depth" className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 — solo link diretti</SelectItem>
                    <SelectItem value="2">2 — + sotto-pagine</SelectItem>
                    <SelectItem value="3">3 — scansione profonda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs text-slate-500">Max pagine</label>
                <Input
                  data-testid="kb-article-maxpages"
                  type="number" min={0} max={500}
                  value={form.crawl_max_pages}
                  onChange={(e) => setForm({ ...form, crawl_max_pages: e.target.value })}
                  className="h-8 w-28 text-xs"
                />
              </div>
              <p className="text-[11px] text-slate-400">0 = non seguire i link. Fino a 500 pagine: più pagine = scansione più ricca ma più lenta.</p>
            </div>
            <Button onClick={save} disabled={saving} data-testid="kb-article-save" className="w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
            </Button>
            <p className="text-xs text-slate-400">Dopo il salvataggio leggo il contenuto della pagina e delle pagine correlate per arricchire l'AI.</p>
          </CardContent>
        </Card>
      )}
      <div className={isAdmin ? "lg:col-span-2 space-y-3" : "lg:col-span-3 space-y-3"}>
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-indigo-600" /> :
          articles.length === 0 ? <p className="text-sm text-slate-500">Nessun articolo salvato.</p> :
            articles.map((a) => {
              const st = a.fetch_status || "pending";
              return (
              <Card key={a.id} data-testid="kb-article-item">
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a href={a.url} target="_blank" rel="noreferrer" className="font-semibold text-slate-900 hover:text-amber-700 inline-flex items-center gap-1">
                      {a.title} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    {a.description && <p className="text-sm text-slate-600 mt-0.5">{a.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                      {(a.concepts || []).map((c) => <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">{c}</span>)}
                    </div>
                    <div className="flex items-center gap-2 mt-2.5">
                      {st === "done" && (
                        <Button size="sm" variant="outline" onClick={() => setReaderId(a.id)} data-testid="kb-article-read" className="h-7 text-xs">
                          <BookOpen className="w-3.5 h-3.5 mr-1" /> Leggi contenuto
                        </Button>
                      )}
                      {st === "pending" && (
                        <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Lettura contenuto in corso…</span>
                      )}
                      {st === "error" && (
                        <span className="text-xs text-rose-500 inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Contenuto non leggibile
                          {isAdmin && <button onClick={() => refetch(a.id)} className="underline ml-1">riprova</button>}
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => del(a.id)} data-testid="kb-article-delete" className="text-slate-400 hover:text-red-600 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </CardContent>
              </Card>
            );})}
      </div>
      {readerId && <ArticleReader articleId={readerId} onClose={() => setReaderId(null)} onOpenOther={(id) => setReaderId(id)} />}
    </div>
  );
}

// Pannello di lettura: contenuto della pagina + pagine correlate (link interni)
// + altri articoli della Knowledge Base con concetti in comune.
function ArticleReader({ articleId, onClose, onOpenOther }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const fetchContent = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/knowledge/articles/${articleId}/content`, ax);
      if (mounted.current) setData(d);
      return d;
    } catch { return null; }
    finally { if (mounted.current) setLoading(false); }
  }, [articleId]);

  useEffect(() => {
    mounted.current = true;
    setLoading(true); setData(null);
    fetchContent();
    return () => { mounted.current = false; };
  }, [fetchContent]);

  // Poll finché il contenuto è in elaborazione.
  useEffect(() => {
    if (!data || data.fetch_status !== "pending") return;
    const iv = setInterval(fetchContent, 4000);
    return () => clearInterval(iv);
  }, [data, fetchContent]);

  return createPortal((
    <div className="fixed inset-0 z-[60] flex" data-testid="kb-article-reader">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full sm:w-[560px] bg-white shadow-2xl h-full overflow-y-auto border-l border-slate-200">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-start justify-between z-10">
          <div className="min-w-0 pr-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">Lettura articolo</div>
            <div className="font-semibold text-slate-900 truncate">{data?.title || "…"}</div>
          </div>
          <button onClick={onClose} data-testid="kb-article-reader-close" className="text-slate-400 hover:text-slate-700 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {loading && <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />}
          {!loading && data && (
            <>
              <a href={data.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:underline text-sm break-all">
                <ExternalLink className="w-4 h-4" /> {data.url}
              </a>
              {data.fetch_status === "pending" && (
                <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sto leggendo il contenuto…</div>
              )}
              {data.fetch_status === "error" && (
                <div className="text-sm text-rose-500 inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {data.fetch_error || "Contenuto non leggibile"}</div>
              )}
              {data.content && (
                <div>
                  {data.page_title && <div className="font-semibold text-slate-800 mb-1">{data.page_title}</div>}
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed max-h-none">{data.content}</div>
                </div>
              )}

              {(data.related || []).length > 0 && (
                <div>
                  <div className="font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><Link2 className="w-4 h-4" /> Pagine correlate ({data.related.length})</div>
                  <div className="space-y-2">
                    {data.related.map((r, i) => <RelatedPage key={i} page={r} />)}
                  </div>
                </div>
              )}

              {(data.related_kb || []).length > 0 && (
                <div>
                  <div className="font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><FileText className="w-4 h-4" /> Articoli correlati in archivio</div>
                  <div className="space-y-1.5">
                    {data.related_kb.map((o) => (
                      <button key={o.id} onClick={() => onOpenOther(o.id)} data-testid="kb-reader-related-kb"
                        className="block w-full text-left text-sm text-indigo-700 hover:underline">
                        {o.title}
                        <span className="text-xs text-slate-400 ml-1">({(o.shared || []).join(", ")})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

function RelatedPage({ page }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg bg-slate-50">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-3 py-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-700 truncate">{page.title}</span>
        <span className="text-xs text-indigo-600 flex-shrink-0">{open ? "Nascondi" : "Leggi"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <a href={page.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline break-all">
            <ExternalLink className="w-3 h-3" /> {page.url}
          </a>
          <div className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">{page.content}</div>
        </div>
      )}
    </div>
  );
}

// ─── COMPONENTE 3 — Regole per dottore ──────────────────────────────────────
function DoctorRules() {
  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState("");
  const [rulesText, setRulesText] = useState("");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/knowledge/doctors`, ax).then(({ data }) => setDoctors(data.doctors || []));
  }, []);

  const loadRule = async (doc) => {
    setDoctor(doc); setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/knowledge/rules/${encodeURIComponent(doc)}`, ax);
      setRulesText(data.rules_text || "");
      setTags((data.tags || []).join(", "));
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!doctor) { toast.error("Seleziona un dottore"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/knowledge/rules/${encodeURIComponent(doctor)}`, {
        rules_text: rulesText, tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      }, ax);
      toast.success("Regole salvate");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4 max-w-2xl">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Dottore</label>
          <Select value={doctor} onValueChange={loadRule}>
            <SelectTrigger data-testid="kb-rule-doctor-select"><SelectValue placeholder="Seleziona un dottore" /></SelectTrigger>
            <SelectContent>
              {doctors.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          {doctors.length === 0 && <p className="text-xs text-slate-400 mt-1">Nessun dottore trovato nei piani di cura.</p>}
        </div>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-indigo-600" /> : doctor && (
          <>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Regole cliniche (protocolli, tariffe, note)</label>
              <Textarea data-testid="kb-rule-text" rows={8} value={rulesText} onChange={(e) => setRulesText(e.target.value)}
                placeholder="Es. Per gli impianti preferisco il carico differito a 3 mesi. Tariffa impianto singolo 950 €..." />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Tag prestazione (opzionale)</label>
              <Input data-testid="kb-rule-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="impianto, ortodonzia" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="kb-rule-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva regole</>}
            </Button>
            <p className="text-xs text-slate-400">Queste regole vengono usate con priorità quando il generatore AI crea un preventivo per questo dottore.</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Pannello admin: indicizzazione ─────────────────────────────────────────
function IndexPanel() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/knowledge/index/status`, ax); setStatus(data); return data; }
    catch { return null; }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Aggiorna sempre il contatore ogni 10s (anche quando l'indicizzazione
    // parte dal salvataggio di un piano o da altra sessione).
    const base = setInterval(fetchStatus, 10000);
    return () => { clearInterval(base); if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.job?.status === "running" && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await fetchStatus();
        if (d?.job?.status !== "running" && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 2500);
    }
  }, [status, fetchStatus]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/api/knowledge/index/start`, { max_photos_per_case: 5 }, ax);
      toast.success("Indicizzazione avviata");
      setTimeout(fetchStatus, 500);
    } catch (e) {
      toast.error(errMsg(e, "Errore nell'avvio"));
    } finally { setStarting(false); }
  };

  const job = status?.job;
  const running = job?.status === "running";
  const remaining = status ? Math.max(0, (status.total_piani || 0) - (status.indexed_cases || 0)) : 0;
  const interrupted = job && (job.status === "interrupted" || job.status === "error") && remaining > 0;
  const pct = status && status.total_piani ? Math.round((status.indexed_cases / status.total_piani) * 100) : 0;

  return (
    <Card className="mb-6">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Database className="w-4 h-4" /> Indicizzazione piani di cura</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {status ? `${status.indexed_cases} casi indicizzati su ${status.total_piani} piani.` : "—"}
              {interrupted ? " Indicizzazione interrotta: premi Riprendi per continuare." : " L'analisi AI delle foto consuma crediti."}
            </p>
          </div>
          <Button onClick={start} disabled={starting || running} data-testid="kb-index-start">
            {running ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> In corso…</>
              : interrupted ? <><RefreshCw className="w-4 h-4 mr-1" /> Riprendi ({remaining} rimanenti)</>
              : <><RefreshCw className="w-4 h-4 mr-1" /> Indicizza tutto</>}
          </Button>
        </div>
        {status && (status.total_piani > 0) && (running || interrupted || job?.status === "completed") && (
          <div className="mt-4" data-testid="kb-index-progress">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>
                {running ? "Elaborazione…" : interrupted ? "Interrotta" : "Completata"} {status.indexed_cases}/{status.total_piani}
                {job?.failed ? ` (${job.failed} errori)` : ""}
              </span>
              <span>{pct}%</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full transition-all ${interrupted ? "bg-amber-500" : "bg-indigo-600"}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Pagina ─────────────────────────────────────────────────────────────────
export default function KnowledgeBasePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="w-full px-4 lg:px-[100px] pb-16">
      <Toaster position="top-right" />
      <PageHeader title="Knowledge Base Clinica" subtitle="Mappa della conoscenza, articoli scientifici e regole per dottore" />
      <div className="bg-white rounded-2xl shadow-sm -mt-8 relative z-10 p-5 sm:p-7">
        {isAdmin && <IndexPanel />}
        <Tabs defaultValue="graph">
          <TabsList data-testid="kb-tabs">
            <TabsTrigger value="graph" data-testid="kb-tab-graph"><Network className="w-4 h-4 mr-1.5" /> Mappa</TabsTrigger>
            <TabsTrigger value="articles" data-testid="kb-tab-articles"><FileText className="w-4 h-4 mr-1.5" /> Articoli</TabsTrigger>
            <TabsTrigger value="rules" data-testid="kb-tab-rules"><Stethoscope className="w-4 h-4 mr-1.5" /> Regole Dottori</TabsTrigger>
          </TabsList>
          <TabsContent value="graph" className="mt-5"><KnowledgeGraph /></TabsContent>
          <TabsContent value="articles" className="mt-5"><ArticlesArchive isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="rules" className="mt-5"><DoctorRules /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
